import { Avatar, Badge, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, List, ListItem, Stack, Tooltip, Typography } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { SidebarProps } from './Sidebar'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import logoImg from '@/assets/img/logo.png'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { logout, selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectNetworkSpeed } from '@/redux/reducers/statistic.reducer'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'

import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import _ from 'lodash'
import colors from '@/assets/scss/_themes-vars.module.scss'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import { styled } from '@mui/material/styles'
import { isStaff } from '@/utils/type-guards'
import { Language } from './Header/Language'

const NETWORK_SPEED_THRESHOLD = 2 // mbps
const HeaderIconButton = styled(IconButton)(({ theme }) => ({
  width: '40px',
  height: '40px',
  background: theme.palette.primary.dark,
  border: `1px solid ${theme.palette.primary.light}`,
  color: theme.palette.background.default,
  '&:hover': {
    background: theme.palette.primary.light,
  },
}))
export function SidebarMobile(props: SidebarProps) {
  const { menuItems } = props
  const [open, setOpen] = useState(false)

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const networkSpeed = useAppSelector(selectNetworkSpeed)
  const dispatch = useAppDispatch()
  const handleLogout = () => {
    dispatch(logout())
    handleClose()
  }
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const authUser = useAppSelector(selectAuthUser)
  const handleClick = (link: string) => {
    navigate(link)
    handleClose()
  }
  return (
    <Box>
      <IconButton onClick={handleOpen} color="inherit" aria-label="open menu" data-testid="mobile-menu-trigger">
        <MenuIcon />
      </IconButton>
      {/* data-testid on the Dialog paper so e2e can await the menu open/close;
          `as never` escapes PaperProps' typed shape to attach a plain DOM attr. */}
      <Dialog fullScreen open={open} onClose={handleClose} PaperProps={{ 'data-testid': 'mobile-menu-dialog' } as never}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Button component={Link} to="/" onClick={() => handleClose()}>
                <img src={authUser?.establishment?.logo?.url ?? logoImg} alt="Logo" height="40" />
              </Button>
            </Box>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close" data-testid="mobile-menu-close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {menuItems.map((item) => {
            const isMenuItemActive = item.isPathBase ? item.link === pathname : pathname.startsWith(item.link)

            return (
              <Fragment key={item.label}>
                <ListItem button onClick={() => handleClick(item.link)} data-testid={`mobile-nav-item-${item.link}`}>
                  <Badge badgeContent={item.badge} color="error" max={99}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        fontWeight: isMenuItemActive ? 'bold' : '400',
                        fontSize: '1rem',
                        fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                      }}
                    >
                      {t(item.label)}
                    </Typography>
                  </Badge>
                </ListItem>
              </Fragment>
            )
          })}
          <Box mt={2} borderTop={1} borderColor="divider">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={2}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="column" alignItems="center" spacing={1} width="100%">
                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                  <CloudDownloadOutlinedIcon fontSize="small" />
                  <Tooltip title={t('header.networkSpeed.download.tooltip')}>
                    <Typography
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        color: !_.isNull(networkSpeed.download) && networkSpeed.download < NETWORK_SPEED_THRESHOLD ? colors.red500 : 'inherit',
                      }}
                    >
                      {!_.isNull(networkSpeed.download) && networkSpeed.download < NETWORK_SPEED_THRESHOLD && <PriorityHighRoundedIcon fontSize="small" />}
                      {networkSpeed.download !== null ? networkSpeed.download : '-'} mbps
                    </Typography>
                  </Tooltip>
                  <CloudUploadOutlinedIcon fontSize="small" />
                  <Tooltip title={t('header.networkSpeed.upload.tooltip')}>
                    <Typography
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        color: !_.isNull(networkSpeed.upload) && networkSpeed.upload < NETWORK_SPEED_THRESHOLD ? colors.red500 : 'inherit',
                      }}
                    >
                      {!_.isNull(networkSpeed.upload) && networkSpeed.upload < NETWORK_SPEED_THRESHOLD && <PriorityHighRoundedIcon fontSize="small" />}
                      {networkSpeed.upload !== null ? networkSpeed.upload : '-'} mbps
                    </Typography>
                  </Tooltip>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" width="80%">
                  <Tooltip title={t('header.accountMenu.notifications')}>
                    <HeaderIconButton color="secondary" onClick={() => handleClick('/notifications')}>
                      <NotificationsNoneRoundedIcon />
                    </HeaderIconButton>
                  </Tooltip>
                  <Language />
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Box mt={2} borderTop={1} borderColor="divider">
            <Box display="flex" alignItems="center" py={2}>
              <Avatar sx={{ width: 40, height: 40, background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}>
                <AccountCircleOutlinedIcon color="primary" />
              </Avatar>
              <Box ml={2}>
                <Typography variant="subtitle1">{authUser?.userInfo.name + ' ' + authUser?.userInfo.surname}</Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {authUser?.email}
                </Typography>
              </Box>
            </Box>
            {/* You can replace the following user navigation items with data from your application */}
            <List>
              <ListItem button onClick={() => handleClick('/configuration')} data-testid="mobile-nav-item-/configuration">
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontWeight: '400',
                    fontSize: '1rem',
                    fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                  }}
                >
                  {t('header.accountMenu.configuration')}
                </Typography>
              </ListItem>
              {isStaff(authUser) && (
                <ListItem
                  button
                  onClick={() => {
                    handleClick('/licenses')
                  }}
                  data-testid="mobile-nav-item-/licenses"
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontWeight: '400',
                      fontSize: '1rem',
                      fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                    }}
                  >
                    {t('header.accountMenu.licenses')}
                  </Typography>
                </ListItem>
              )}
              <ListItem button onClick={() => handleLogout()} data-testid="mobile-nav-logout">
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontWeight: '400',
                    fontSize: '1rem',
                    fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                  }}
                >
                  {t('header.accountMenu.logout')}
                </Typography>
              </ListItem>
            </List>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
